<script setup>
// Book a playing area (BOOKINGS_PLAN.md B5, B10, B13, B17, B18, BE17, BE27;
// X47, Grant 2026-10-19): one area group at a time (tabs), a Mon–Sun strip
// paged a week at a time within the viewer's window that picks the day
// (?date=), a day grid with a column per playing area, a legend that explains
// itself, cells whose state comes from the API's per-cell status and per-slot
// bookable flag plus the viewer's own allowance (a + only where a tap really books), the booking
// sheet for members and the visitor sheet signed out (inside the club-branded
// frame, when a group allows visitors). Live: a signed-in grid listens on the
// club's SignalR group; a signed-out one polls. One availability call per week
// (GET availability?from=<Monday>&days=7), cached while the page is open.
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import ClubBrandedFrame from '~/components/clubs/ClubBrandedFrame.vue';
import FrontendHeader from '~/components/layout/FrontendHeader.vue';
import BookingPageBody from '~/components/bookings/BookingPageBody.vue';
import BookingSheet from '~/components/bookings/BookingSheet.vue';
import VisitorBookingDialog from '~/components/bookings/VisitorBookingDialog.vue';
import {useAuthService} from '~/composables/useAuthService';
import {useHeadService} from '~/composables/useHeadService';
import {useBookingsMember, useLiveBookings} from '~/composables/useBookingsMember';
import {safeReturnUrl} from '~/utils/membershipsMember';
import {BOOKING_ICON, buildGrid, dateInZone} from '~/utils/bookingsMember';
import {memberLegend} from '~/utils/bookingLegend';
import {aOrAn, areaWords} from '~/utils/areaWords';
import {
  allowanceChips, bookQuery, bookTitle, dayGrid, dayStrip, groupAllowance, groupTabs, jumpWindow, legendKeys, pickTab, sameQuery, shownDate, weekLabel, weekNav, weekStart,
} from '~/utils/bookingWeek';

definePageMeta({
  name: 'ClubBook',
  path: '/clubs/:clubId/book',
  middleware: ['booking-layout'],
});

const route = useRoute();
const router = useRouter();
const {isAuthenticated} = useAuthService();
const {setMeta} = useHeadService();
const {setAlertMessage} = useAlertService();
const api = useBookingsMember();
// Grant 2026-11-02: nothing is known yet, so no noun; the club's own title and noun replace this once it loads.
setMeta('Book | Tap Leagues', 'See what is available and book.');

const club = ref(null);       // page/club's club
const players = ref([]);      // the club's members, for "playing with"
const publicPage = ref(null); // signed out: branding, visitors, return link
const weeks = ref({});        // Monday → that week's availability
const base = ref(null);       // the latest availability (viewer, groups, areas, sports, zone)
const gridLoading = ref(false);
const notFound = ref(false);
const failed = ref(null);
const touch = ref(false);
const nowIso = ref(new Date().toISOString());
let clock = null;

const clubId = computed(() => base.value?.clubId ?? club.value?.id ?? null);
const signedIn = computed(() => isAuthenticated.value);
const sportId = computed(() => (route.query.sport ? String(route.query.sport) : null));
const todayDate = computed(() => dateInZone(base.value?.timeZone));
const daysAhead = computed(() => base.value?.viewer?.daysAhead ?? 7);
const wantedDate = computed(() => (route.query.date ? String(route.query.date) : null));
const shown = computed(() => shownDate(wantedDate.value, todayDate.value, daysAhead.value));
const monday = computed(() => shown.value.monday);
const weekData = computed(() => (monday.value ? weeks.value[monday.value] ?? null : null));
const availability = computed(() => weekData.value ?? base.value);

const loadWeek = async (from, {quiet = false} = {}) => {
  if (!clubId.value || !from) return;
  if (!quiet) gridLoading.value = true;
  try {
    const res = await api.availability(clubId.value, {from, days: 7, sportId: sportId.value});
    weeks.value = {...weeks.value, [res.from ?? from]: res};
    base.value = res;
    failed.value = null;
  } catch {
    failed.value = "The grid couldn't be loaded. Please try again.";
  } finally {
    gridLoading.value = false;
  }
};
const reloadAll = (quiet = false) => { weeks.value = {}; return loadWeek(monday.value, {quiet}); };

onMounted(async () => {
  try { touch.value = !!window.matchMedia?.('(hover: none)')?.matches; } catch { touch.value = false; }
  clock = setInterval(() => { nowIso.value = new Date().toISOString(); }, 60000);
  try {
    const page = await api.clubPage(route.params.clubId);
    club.value = page?.club ?? null;
    players.value = page?.players ?? [];
    if (!club.value) { notFound.value = true; return; }
    if (!signedIn.value) {
      try { publicPage.value = await api.publicPage(club.value.slug, route.query.returnUrl ? String(route.query.returnUrl) : null); } catch { publicPage.value = null; }
    }
    // The club's zone isn't known until the first answer: start from the week of the day asked for (or the browser's today).
    let guess = null;
    try { guess = dateInZone(Intl.DateTimeFormat().resolvedOptions().timeZone); } catch { guess = dateInZone(null); }
    const first = weekStart(wantedDate.value) ?? weekStart(guess);
    gridLoading.value = true;
    const res = await api.availability(club.value.id, {from: first, days: 7, sportId: sportId.value});
    weeks.value = {[res.from ?? first]: res};
    base.value = res;
    gridLoading.value = false;
    if (monday.value && !weeks.value[monday.value]) await loadWeek(monday.value);
    setMeta(`${title.value} · ${club.value.name}`, `See what is available at ${club.value.name} and book ${aOrAn(noun.value)}.`);
  } catch (e) {
    gridLoading.value = false;
    if (e?.status === 404 || e?.statusCode === 404) notFound.value = true;
    else failed.value = "This page couldn't be loaded. Please try again.";
  }
});
onBeforeUnmount(() => { if (clock) clearInterval(clock); });

// A week not loaded yet (paging, or a ?date= further out): fetch it.
watch(monday, (m) => { if (base.value && m && !weeks.value[m]) loadWeek(m); });
watch(sportId, () => { if (base.value) reloadAll(); });

// ── Live (BE27) ──
useLiveBookings(clubId, {signedIn, onChanged: () => reloadAll(true)});

// ── The viewer ──
const viewer = computed(() => base.value?.viewer ?? null);
const visitorsAllowed = computed(() => !!publicPage.value?.visitorsAllowed);
const canTap = computed(() => signedIn.value ? !!viewer.value?.canBook : visitorsAllowed.value);
// Grant 2026-11-02: the club's own words ("court", "court or board"), "playing area" only when nothing names anything.
const noun = computed(() => areaWords(base.value).noun);
// "Book a court" / "Book a table or court" from the nouns of the areas shown (D2 item 8); "Book" until the club loads.
const title = computed(() => (base.value ? bookTitle(base.value, sportId.value) : 'Book'));

// ── Tabs (one slot step each), the week and the day (all in the address via router.replace) ──
const tabs = computed(() => groupTabs(base.value, sportId.value));
const tab = computed(() => pickTab(tabs.value, route.query.group ? String(route.query.group) : null));
const allowance = computed(() => {
  if (!tab.value) return null;
  if (signedIn.value) return groupAllowance(viewer.value, tab.value.id);
  return publicPage.value?.groups?.find((g) => g.id === tab.value.id)?.visitor ?? null;
});
const priceMinor = computed(() => allowance.value?.priceMinor ?? (signedIn.value ? viewer.value?.priceMinor : null) ?? null);
const visitorsReason = computed(() => publicPage.value?.visitorsReason ?? null);
const tapReason = computed(() => signedIn.value ? (viewer.value?.reason || "You can't book here.") : `Sign in to book. ${visitorsReason.value || "Visitors can't book here yet."}`);
const cellCtx = computed(() => ({
  viewer: viewer.value, allowance: allowance.value, canTap: canTap.value, tapReason: tapReason.value, priceMinor: priceMinor.value,
  currency: viewer.value?.currency ?? publicPage.value?.currency ?? 'gbp', now: nowIso.value, noun: tab.value?.noun,
  // X54 (D2 H1): a priced cell the club can't take a card for reads Can't book with this reason.
  paymentsRefusal: viewer.value?.paymentsRefusal ?? null,
}));
const grid = computed(() => dayGrid(weekData.value?.dayList ?? [], shown.value.date, tab.value, cellCtx.value));
const days = computed(() => dayStrip(monday.value, weekData.value?.dayList ?? [], {todayDate: todayDate.value, selectedDate: shown.value.date, tab: tab.value, ...cellCtx.value}));
const nav = computed(() => weekNav(monday.value, todayDate.value, daysAhead.value));
const label = computed(() => weekLabel(monday.value));
// X88: the strip's "Jump to a date" keeps to the days the strip can page to (today to the last pageable week).
const jump = computed(() => jumpWindow(todayDate.value, daysAhead.value));
const notice = computed(() => (base.value && !gridLoading.value ? shown.value.notice : null));
// X54: the earlier rows of today fold away; the legend lists only the states on screen.
const pastOpen = ref(false);
watch(() => shown.value.date, () => { pastOpen.value = false; });
const foldable = computed(() => (grid.value?.startedRows ?? 0) > 0 && (grid.value?.startedRows ?? 0) < (grid.value?.rows?.length ?? 0));
const legendItems = computed(() => {
  const keys = legendKeys(grid.value?.columns ?? [], {pastShown: pastOpen.value || !foldable.value});
  // Grant 2026-11-02: the legend says the tab's own noun ("closed this court").
  return memberLegend(tab.value?.noun || noun.value).filter((l) => keys.includes(l.key));
});
const chips = computed(() => (signedIn.value && base.value?.bookingsEnabled ? allowanceChips(viewer.value, allowance.value, tab.value?.cancelCutoffMinutes ?? null) : {chips: [], limitLine: null}));

const setQuery = (patch) => {
  const next = bookQuery(route.query, {
    date: 'date' in patch ? patch.date : (wantedDate.value ?? null),
    groupId: 'group' in patch ? patch.group : (tab.value?.id ?? null),
    todayDate: todayDate.value, firstGroupId: tabs.value[0]?.id ?? null,
  });
  if ('sport' in patch) { if (patch.sport) next.sport = patch.sport; else delete next.sport; }
  if (!sameQuery(next, route.query)) router.replace({query: next});
};
// Paging a week lands on today when the week holds it, else on its Monday; Today goes back to today.
const pickWeek = (to) => {
  if (!to) return setQuery({date: null});
  setQuery({date: weekStart(todayDate.value) === to ? null : to});
};
const pickDay = (date) => setQuery({date});
const pickGroup = (id) => setQuery({group: id});
const pickSport = (id) => setQuery({sport: id});

// ── Tapping a cell ──
const pick = ref(null);
const sheetOpen = ref(false);
const visitorOpen = ref(false);
const onTap = ({cell, column, slots}) => {
  const day = (weekData.value?.dayList ?? []).find((d) => d.date === cell.date);
  const area = column?.area ?? (base.value?.areas ?? []).find((a) => a.id === cell.areaId);
  const group = (base.value?.groups ?? []).find((g) => g.id === tab.value?.id) ?? null;
  if (!day || !area) return;
  const col = buildGrid(day, {groups: base.value.groups, areas: base.value.areas, sportId: sportId.value}).columns.find((c) => c.area.id === area.id);
  const index = col?.cells.findIndex((c) => c.slot.startsAt === cell.startsAt) ?? -1;
  if (!col || index < 0) return;
  // X54 (ruling 5): a dragged range opens the sheet at its length (X50's initialSlots, set on the pick in the same tick).
  pick.value = {area, slot: col.cells[index].slot, cell: col.cells[index].cell, group, column: col, index, date: cell.date, slots: Math.max(1, slots ?? 1)};
  if (signedIn.value) sheetOpen.value = true;
  else visitorOpen.value = true;
};
// A cell that can't be booked: on a phone (no hover) its reason is shown as a toast; a mouse has the tooltip. A dragged range
// dropped where it can't be booked says why on every device.
const onExplain = ({cell, reason}) => {
  if (reason) setAlertMessage({message: reason, color: 'error'});
  else if (touch.value && cell?.tip) setAlertMessage({message: cell.tip, color: 'info'});
};
// A week arrow that can't go says why when pressed (ruling 3).
const onBlocked = (hint) => { if (hint) setAlertMessage({message: hint, color: 'info'}); };
const onBooked = () => { reloadAll(true); };
const selected = computed(() => (sheetOpen.value || visitorOpen.value) && pick.value ? {areaId: pick.value.area.id, startsAt: pick.value.slot.startsAt, slots: pick.value.slots ?? 1} : null);
const sheetClub = computed(() => club.value ? {id: club.value.id, name: club.value.name, players: players.value} : null);

// ── Lines ──
const footLine = computed(() => {
  if (!base.value?.bookingsEnabled || signedIn.value) return null;
  return visitorsAllowed.value ? `Visitors: ${publicPage.value.visitorPriceLine || 'free'} · one booking at a time.` : null;
});
const reasonLine = computed(() => {
  if (!signedIn.value || !viewer.value) return null;
  if (!viewer.value.canBook) return viewer.value.reason;
  // X74 (R25 M1): this tab's own reason ("Membership or pass needed to book here.") when the group refuses the viewer
  // while another lets them in; the at-limit alert is per tab too (allowanceChips).
  const a = allowance.value;
  return a && a.canBook === false && a.reason ? a.reason : null;
});

// ── Branding (signed out, B17/SE18) ──
const brandClub = computed(() => publicPage.value ? {
  name: publicPage.value.clubName, imageFileName: publicPage.value.clubImageFileName ?? club.value?.imageFileName ?? null,
  brandColour: publicPage.value.clubBrandColour ?? null, isVerified: !!publicPage.value.clubIsVerified, websiteUrl: publicPage.value.clubWebsiteUrl ?? null,
} : (club.value ? {name: club.value.name, imageFileName: club.value.imageFileName, isVerified: false} : null));
const back = computed(() => {
  const raw = publicPage.value?.returnUrl || (route.query.returnUrl ? String(route.query.returnUrl) : null);
  return safeReturnUrl(raw, {websiteUrl: brandClub.value?.websiteUrl ?? null, allowedHosts: publicPage.value?.returnHost ? [publicPage.value.returnHost] : []});
});
const signInTo = computed(() => ({path: '/login', query: {redirectUrl: route.fullPath}}));
const bodyProps = computed(() => ({
  availability: availability.value, tabs: tabs.value, tab: tab.value, grid: grid.value, days: days.value,
  weekLabel: label.value, nav: nav.value, jumpMin: jump.value.min, jumpMax: jump.value.max, chips: chips.value, legendItems: legendItems.value, sports: base.value?.sports ?? [], sportId: sportId.value,
  allowance: allowance.value, priceMinor: priceMinor.value, currency: cellCtx.value.currency,
  gridLoading: gridLoading.value, footLine: footLine.value, reasonLine: reasonLine.value, failed: failed.value, notice: notice.value,
  signedIn: signedIn.value, visitorsAllowed: visitorsAllowed.value, visitorsReason: visitorsReason.value, signInTo: signInTo.value, selected: selected.value,
  pastOpen: pastOpen.value, touch: touch.value,
}));
const bodyEvents = {pickGroup, pickDay, pickWeek, pickDate: pickDay, pickSport, tap: onTap, explain: onExplain, blocked: onBlocked, 'update:pastOpen': (v) => { pastOpen.value = v; }};
</script>

<!-- One root node and nothing else at the top of the template: in dev Vue keeps comments, so a comment beside the
     root makes a two-node fragment and the page goes blank when a route changes under it (TEST_T2 H1, R4 H1). -->
<template>
  <div class="bk-root">
    <!-- Signed in: the site's chrome -->
    <base-page v-if="signedIn">
      <v-container class="large bk-container">
        <div v-if="notFound" class="tl-card bk-center">
          <v-icon size="40" color="charcoal" icon="mdi-map-marker-off-outline"/>
          <div class="bk-title">Club not found</div>
        </div>
        <template v-else>
          <!-- X202 (R82 L13): the header is drawn from the first paint (the club arrives on mount; the crumbs and crest
               fill in), so it never pushes the page down when it loads -->
          <frontend-header :club="club" :image-file-name="club?.imageFileName ?? null" :title="title" :title-icon="BOOKING_ICON">
            <template #subTitle>
              <span class="bk-sub bk-sub--line">{{ club?.name }}<template v-if="base?.timeZoneLabel"> · {{ base.timeZoneLabel }}</template><template v-if="tab && tabs.length === 1 && tab.label && tab.label !== title.slice(7)"> · {{ tab.label }}</template></span>
            </template>
          </frontend-header>
          <booking-page-body v-bind="bodyProps" v-on="bodyEvents"/>
          <div class="bk-links">
            <nuxt-link :to="{path: '/your-profile', query: {tab: 'bookings'}}">Your bookings</nuxt-link>
            <span class="bk-dot">·</span>
            <nuxt-link v-if="club" :to="'/clubs/' + club.id">Back to {{ club.name }}</nuxt-link>
          </div>
        </template>
      </v-container>
      <booking-sheet v-model="sheetOpen" :pick="pick" :initial-slots="pick?.slots ?? 1" :noun="tab?.noun || noun" :club="sheetClub" :viewer="viewer" :date="pick?.date ?? todayDate" :today-date="todayDate" :sport-id="sportId" @booked="onBooked"/>
    </base-page>

    <!-- Signed out: the club-branded visitor page (B17) -->
    <club-branded-frame v-else :club="brandClub" :return-url="back?.href ?? null" credit="Bookings and payments by Tap Leagues" width="wide">
      <div v-if="notFound" class="tl-card bk-center">
        <v-icon size="40" color="charcoal" icon="mdi-map-marker-off-outline"/>
        <div class="bk-title">Club not found</div>
      </div>
      <template v-else>
        <div class="bk-public-head">
          <div class="bk-title">{{ title }}</div>
          <div class="bk-sub">
            <template v-if="club">{{ club.name }}</template><template v-if="base?.timeZoneLabel"> · {{ base.timeZoneLabel }}</template>
            <template v-if="club"> · <nuxt-link :to="signInTo" class="bk-link">Sign in</nuxt-link></template>
          </div>
        </div>
        <booking-page-body v-bind="bodyProps" v-on="bodyEvents"/>
      </template>
      <visitor-booking-dialog v-model="visitorOpen" :pick="pick" :initial-slots="pick?.slots ?? 1" :page="publicPage" :date="pick?.date ?? todayDate" :today-date="todayDate" :sport-id="sportId" :back="back" @booked="onBooked"/>
    </club-branded-frame>
  </div>
</template>

<style scoped lang="scss">
.bk-center { text-align: center; padding: 36px 24px; }
.bk-title { font-size: 18px; font-weight: 800; letter-spacing: -0.3px; }
.bk-sub { font-size: 13px; color: rgb(var(--v-theme-textSubtle)); }
/* X202 (R82 L13): the header's line keeps its height while the club's name loads */
.bk-sub--line { display: block; min-height: 1.5em; }
.bk-public-head { margin: 0 0 14px; }
.bk-link { color: rgb(var(--v-theme-primary)); font-weight: 600; }
.bk-links { margin-top: 18px; font-size: 12.5px; font-weight: 600; a { color: rgb(var(--v-theme-primary)); text-decoration: none; } }
.bk-dot { margin: 0 8px; color: rgb(var(--v-theme-textSubtle)); }
</style>
